import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import MainContent from './components/MainContent';
import MultipleUploadContent from './components/MultipleUploadContent';
import SmartParserContent from './components/SmartParserContent';
import FinancialAnalysisContent from './components/FinancialAnalysisContent';
import AuditPlanContent from './components/AuditPlanContent';
import RnDPromptingContent from './components/RnDPromptingContent';
import LLMConfigContent from './components/LLMConfigContent';
import PrivacySecurityContent from './components/PrivacySecurityContent';
import DocumentationContent from './components/DocumentationContent';
import ContactUsContent from './components/ContactUsContent';
import HistoryContent from './components/HistoryContent';
import AnalyticsContent from './components/AnalyticsContent';
import AdminAreaContent from './components/AdminAreaContent';
import AccountContent from './components/AccountContent';
import DashboardContent from './components/DashboardContent';
import FinSightContent from './components/FinSightContent';
import SmartFinSightContent from './components/SmartFinSightContent';
import FloatingChatbot from './components/FloatingChatbot';
import TrustSection from './components/TrustSection';
import { isAuthenticated, getUser, clearAuth, saveUser, getPagesPermissions, getAuthHeaders } from './utils/auth';
import { getFullApiUrl } from './config';

function App() {
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
  const [user, setUser] = useState(null);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [pagesPermissions, setPagesPermissions] = useState([]);
  const [isFinancialAnalysisProcessing, setIsFinancialAnalysisProcessing] = useState(false);
  const [isSmartParserProcessing, setIsSmartParserProcessing] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Function to fetch latest user data from API
  const fetchLatestUserData = async () => {
    try {
      const response = await fetch(getFullApiUrl('ME'), {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        saveUser(userData); // Update localStorage with latest data
      }
    } catch (err) {
      console.error('Error fetching latest user data:', err);
      // Continue with existing user data if fetch fails
    }
  };

  const loadPagesPermissions = async () => {
    try {
      const response = await fetch(getFullApiUrl('MY_PAGES'), {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setPagesPermissions(data.pages || []);
      }
    } catch (err) {
      console.error('Error fetching pages permissions:', err);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticatedState(true);
    setShowLandingPage(false);
    setShowLogin(false);
    // Always redirect to dashboard after login
    setActiveMenu('dashboard');
    
    // Token and user already saved in Login component via auth utils
    // Load pages permissions after login - prefer permissions from callback, then localStorage, then fetch
    if (userData.permissions && userData.permissions.length > 0) {
      setPagesPermissions(userData.permissions);
    } else {
      const savedPermissions = getPagesPermissions();
      if (savedPermissions && savedPermissions.length > 0) {
        setPagesPermissions(savedPermissions);
      } else {
        loadPagesPermissions();
      }
    }
    
    // Fetch latest user data from API to ensure we have the most up-to-date info
    fetchLatestUserData();
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticatedState(false);
    setShowLandingPage(true);
    setShowLogin(false);
    clearAuth(); // Clear token and user from localStorage
  };

  const handleMenuChange = (menuId) => {
    // Prevent navigation if financial analysis is processing
    if (isFinancialAnalysisProcessing && menuId !== 'financial-analysis') {
      const confirmed = window.confirm(
        'Proses analisis laporan keuangan sedang berjalan.\n\n' +
        'Jika Anda meninggalkan halaman ini, hasil analisis mungkin akan hilang dan Anda perlu memproses ulang.\n\n' +
        'Apakah Anda yakin ingin meninggalkan halaman ini?'
      );
      if (!confirmed) {
        return; // Cancel navigation
      }
    }
    
    // Prevent navigation if smart parser is processing
    if (isSmartParserProcessing && menuId !== 'smart-parser') {
      const confirmed = window.confirm(
        'Proses parsing sedang berjalan.\n\n' +
        'Jika Anda meninggalkan halaman ini, hasil parsing mungkin akan hilang dan Anda perlu memproses ulang.\n\n' +
        'Apakah Anda yakin ingin meninggalkan halaman ini?'
      );
      if (!confirmed) {
        return; // Cancel navigation
      }
    }
    
    setActiveMenu(menuId);
  };

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return <DashboardContent pagesPermissions={pagesPermissions} user={user} onMenuChange={handleMenuChange} />;
      case 'smart-parser':
        return <SmartParserContent onProcessingStateChange={setIsSmartParserProcessing} />;
      case 'financial-analysis':
        return <FinancialAnalysisContent onProcessingStateChange={setIsFinancialAnalysisProcessing} />;
      case 'audit-plan-analysis':
        return <AuditPlanContent />;
      case 'finsight':
        return <FinSightContent />;
      case 'smart-finsight':
        return <SmartFinSightContent />;
      case 'rnd-prompting':
        return <RnDPromptingContent />;
      case 'llm-config':
        return <LLMConfigContent />;
      case 'privacy-security':
        return <PrivacySecurityContent />;
      case 'documentation':
        return <DocumentationContent pagesPermissions={pagesPermissions} user={user} />;
      case 'contact-us':
        return <ContactUsContent />;
      case 'history':
        return <HistoryContent />;
      case 'analytics':
        return <AnalyticsContent />;
      case 'admin-area':
        return <AdminAreaContent />;
      case 'my-account':
        return <AccountContent onUserUpdate={handleUserUpdate} />;
      // Hidden menus (commented out)
      // case 'multiple-parser':
      //   return <MultipleUploadContent />;
      // case 'single-parser':
      //   return <MainContent />;
      default:
        return <DashboardContent pagesPermissions={pagesPermissions} user={user} onMenuChange={handleMenuChange} />;
    }
  };

  const handleUserUpdate = (userData) => {
    setUser(userData);
    saveUser(userData);
  };

  // Load dark mode preference on mount
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Toggle dark mode function
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      if (isAuthenticated()) {
        const savedUser = getUser();
        if (savedUser) {
          setUser(savedUser);
          setIsAuthenticatedState(true);
          setShowLandingPage(false);
          setShowLogin(false);
          // Load pages permissions
          const savedPermissions = getPagesPermissions();
          if (savedPermissions && savedPermissions.length > 0) {
            setPagesPermissions(savedPermissions);
          } else {
            loadPagesPermissions();
          }
        } else {
          setShowLandingPage(true);
          setShowLogin(false);
        }
      } else {
        setShowLandingPage(true);
        setShowLogin(false);
      }
    };
    checkAuth();
  }, []);

  // Navigation handlers for landing page
  const handleNavigateToLogin = () => {
    setShowLandingPage(false);
    setShowLogin(true);
  };

  const handleBackToLanding = () => {
    setShowLandingPage(true);
    setShowLogin(false);
  };

  const handleNavigateToTryProduct = () => {
    // If authenticated, go to dashboard, otherwise go to login
    if (isAuthenticatedState) {
      setShowLandingPage(false);
      setShowLogin(false);
      setActiveMenu('dashboard');
    } else {
      setShowLandingPage(false);
      setShowLogin(true);
    }
  };

  const handleNavigateToPricing = () => {
    // Scroll to pricing section if on landing page, otherwise show login
    if (showLandingPage) {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (!isAuthenticatedState) {
      handleNavigateToLogin();
    }
  };

  // Show landing page if not authenticated and not showing login
  if (!isAuthenticatedState && !showLogin) {
    return (
      <LandingPage
        onNavigateToLogin={handleNavigateToLogin}
        onNavigateToTryProduct={handleNavigateToTryProduct}
        onNavigateToPricing={handleNavigateToPricing}
      />
    );
  }

  // Show login page if not authenticated and showLogin is true
  if (!isAuthenticatedState && showLogin) {
    return <Login onLogin={handleLogin} onBackToLanding={handleBackToLanding} />;
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden transition-colors duration-300">
      {/* Fixed Sidebar */}
      <Sidebar 
        activeMenu={activeMenu} 
        onMenuChange={handleMenuChange}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
        pagesPermissions={pagesPermissions}
        user={user}
      />
      
      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col h-screen transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-20' : 'ml-64'
      }`}>
        {/* Fixed Header */}
        <Header activeMenu={activeMenu} user={user} onLogout={handleLogout} onMenuChange={handleMenuChange} isDarkMode={isDarkMode} onToggleDarkMode={toggleDarkMode} />
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto pb-20">
          {renderContent()}
        </div>
      </div>
      
      {/* Sticky Footer */}
      <TrustSection isCollapsed={isSidebarCollapsed} onNavigate={handleMenuChange} />
      
      <FloatingChatbot onNavigate={handleMenuChange} />
    </div>
  );
}

export default App; 